import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { appContext } from 'common/contexts';
import { Routes as R } from 'common/enums';
import { InventoryItem, Item } from 'common/types';
import { ArrowLeftTwoTone } from 'common/icons';
import styles from './inventoryItemEditor.module.css';

type InventoryItemEditorParams = {
  id?: string;
};

const InventoryItemEditor: React.FC = () => {
  const { id } = useParams<InventoryItemEditorParams>();
  const history = useHistory();
  const { state, actions, helpers } = useContext(appContext);
  const { inventoryItems = [], items = [] } = state;
  const { translation } = helpers;
  const { t } = translation;
  
  // Filtrar productos no eliminados para el selector
  const availableProducts = items.filter(item => !item.isDeleted);
  
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState<Omit<InventoryItem, 'id' | 'lastModifiedTime' | 'isDeleted' | 'ownerId' | 'parentUserId'>>({ 
    name: '',
    description: '',
    type: 'raw',
    unit: 'kg',
    quantity: 0,
    minQuantity: 0,
    costPrice: 0,
    associatedProductId: undefined
  });

  // Load item data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const item = inventoryItems.find(item => item.id === id);
      if (item) {
        setFormData({
          name: item.name,
          description: item.description || '',
          type: item.type,
          unit: item.unit,
          quantity: item.quantity,
          minQuantity: item.minQuantity,
          costPrice: item.costPrice,
          associatedProductId: item.associatedProductId
        });
      } else {
        // Item not found, redirect to list
        history.push(R.InventoryItemsList);
      }
    }
  }, [isEditMode, id, inventoryItems, history]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = t('common.errors.required');
    }
    
    if (formData.quantity < 0) {
      errors.quantity = t('common.errors.positiveNumber');
    }
    
    if (formData.minQuantity < 0) {
      errors.minQuantity = t('common.errors.positiveNumber');
    }
    
    if (formData.costPrice < 0) {
      errors.costPrice = t('common.errors.positiveNumber');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Convert numeric inputs to numbers
    if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : Number(value)
      }));
    } else if (name === 'associatedProductId') {
      // Para el selector de productos, usar undefined si no se seleccionó nada
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? undefined : value
      }));
      
      // Si se seleccionó un producto, actualizar automáticamente nombre, unidad y precio de costo
      if (value) {
        const selectedProduct = items.find(item => item.id === value);
        if (selectedProduct) {
          setFormData(prev => ({
            ...prev,
            name: selectedProduct.name,
            unit: selectedProduct.unit,
            costPrice: selectedProduct.costPrice
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Para compatibilidad con Firestore, omitimos el campo associatedProductId si es undefined
      const inventoryData = { ...formData };
      
      // Si el tipo es materia prima o no se seleccionó un producto, eliminamos el campo
      if (formData.type === 'raw' || !formData.associatedProductId) {
        delete inventoryData.associatedProductId;
      }
      
      if (isEditMode && id) {
        // Update existing item
        await actions.inventory.updateInventoryItem({
          ...inventoryData,
          id,
          lastModifiedTime: Date.now(),
          isDeleted: false
        });
        toast.success(t('admin.inventory.itemEditor.itemUpdatedSuccess'));
      } else {
        // Create new item
        await actions.inventory.addInventoryItem(inventoryData);
        toast.success(t('admin.inventory.itemEditor.itemCreatedSuccess'));
      }
      
      // Redirect to items list
      history.push(R.InventoryItemsList);
    } catch (error) {
      console.error('Error saving inventory item:', error);
      toast.error(t('admin.inventory.itemEditor.errorSavingItem'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={R.InventoryItemsList} className={styles.backButton}>
            <ArrowLeftTwoTone />
          </Link>
          <h1 className={styles.title}>
            {isEditMode 
              ? t('admin.inventory.itemEditor.editItemTitle')
              : t('admin.inventory.itemEditor.addItemTitle')}
          </h1>
        </div>
      </div>
      
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="name">
            {t('admin.inventory.itemEditor.nameLabel')}
          </label>
          <input
            id="name"
            name="name"
            type="text"
            className={styles.input}
            value={formData.name}
            onChange={handleChange}
            required
          />
          {formErrors.name && <div className={styles.error}>{formErrors.name}</div>}
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="description">
            {t('admin.inventory.itemEditor.descriptionLabel')}
          </label>
          <textarea
            id="description"
            name="description"
            className={styles.textarea}
            value={formData.description}
            onChange={handleChange}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="type">
            {t('admin.inventory.itemEditor.typeLabel')}
          </label>
          <select
            id="type"
            name="type"
            className={styles.select}
            value={formData.type}
            onChange={handleChange}
          >
            <option value="raw">{t('admin.inventory.items.raw')}</option>
            <option value="finished">{t('admin.inventory.items.finished')}</option>
          </select>
        </div>
        
        {/* Selector de producto asociado, solo visible para productos terminados */}
        {formData.type === 'finished' && (
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="associatedProductId">
              {t('admin.inventory.itemEditor.associatedProductLabel', 'Producto asociado')}
            </label>
            <select
              id="associatedProductId"
              name="associatedProductId"
              className={styles.select}
              value={formData.associatedProductId || ''}
              onChange={handleChange}
            >
              <option value="">{t('admin.inventory.itemEditor.selectProduct', 'Seleccionar producto...')}</option>
              {availableProducts
                .filter(product => product.type !== 'group') // Excluir productos de tipo group
                .map(product => (
                  <option key={product.id} value={product.id}>
                    {product.itemParentName ? `${product.itemParentName} ${product.name}` : product.name}
                  </option>
                ))}
            </select>
            <p className={styles.helper}>
              {t('admin.inventory.itemEditor.associatedProductHelper', 'Al asociar un producto, se actualizará automáticamente el nombre y la unidad.')}
            </p>
          </div>
        )}
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="unit">
            {t('admin.inventory.itemEditor.unitLabel')}
          </label>
          <select
            id="unit"
            name="unit"
            className={styles.select}
            value={formData.unit}
            onChange={handleChange}
          >
            <option value="kg">kg</option>
            <option value="g">g</option>
            <option value="l">l</option>
            <option value="ml">ml</option>
            <option value="units">{t('common.each')}</option>
          </select>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="quantity">
            {t('admin.inventory.itemEditor.quantityLabel')}
          </label>
          <input
            id="quantity"
            name="quantity"
            type="number"
            step="0.01"
            className={styles.input}
            value={formData.quantity}
            onChange={handleChange}
            required
          />
          {formErrors.quantity && <div className={styles.error}>{formErrors.quantity}</div>}
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="minQuantity">
            {t('admin.inventory.itemEditor.minQuantityLabel')}
          </label>
          <input
            id="minQuantity"
            name="minQuantity"
            type="number"
            step="0.01"
            className={styles.input}
            value={formData.minQuantity}
            onChange={handleChange}
            required
          />
          {formErrors.minQuantity && <div className={styles.error}>{formErrors.minQuantity}</div>}
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="costPrice">
            {t('admin.inventory.itemEditor.costPriceLabel')}
          </label>
          <input
            id="costPrice"
            name="costPrice"
            type="number"
            step="0.01"
            className={styles.input}
            value={formData.costPrice}
            onChange={handleChange}
            required
          />
          {formErrors.costPrice && <div className={styles.error}>{formErrors.costPrice}</div>}
        </div>
        
        <div className={styles.buttonGroup}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading 
              ? t('common.saving')
              : isEditMode 
                ? t('common.update')
                : t('common.create')}
          </button>
          
          <Link to={R.InventoryItemsList} className={styles.cancelButton}>
            {t('common.cancel')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default InventoryItemEditor;
